// Data-honesty helpers — "show accurate data, or clearly flag what we don't
// know; never show a value we can't stand behind."
//
// Why this exists: a live review found properties that actively mislead —
//   - 6019 INGLESIDE DR: property_type "Commercial" but property_class
//     "residential" (res_plus symbology), 1,557 sqft, built 1960. A house
//     labeled Commercial.
//   - 2608 GOLF COURSE LN: property_class "land_only" but 3 beds / 1,174 sqft.
//     Vacant land that has a house on it.
//   - Cabarrus / Lincoln rows with no beds, lot size, amount due, or zip shown
//     with no signal that the data is thin.
//
// Neither property_type nor property_class is reliably correct on its own, so
// these helpers reconcile them against the strongest signal we have — the
// physical STRUCTURE (bedrooms, square footage, year built) — and surface what
// is missing. The durable cross-source fix is the field resolver (Wave 0c);
// this is the honest display layer until then.

import { Property } from "../types";
import { propertyClassLabel } from "./propertyLabels";
import { statusMeta } from "./statusMeta";
import { leadTypeMeta, primaryLeadSignal } from "./leadTypeMeta";

// ── property type reconciliation ────────────────────────────────────────────

export interface ResolvedType {
  /** The type label to display, or null when nothing is known. */
  label: string | null;
  /** True when sources disagreed and we overrode/distrusted the raw type. */
  conflict: boolean;
  /** Short explanation of the conflict, for a secondary line / data note. */
  note?: string;
}

const COMMERCIAL_TYPE_RE = /commercial|industrial|office|retail|warehouse/i;
// Land/vacant raw-type strings — a structure on record contradicts these.
const VACANT_TYPE_RE = /vacant|raw land|\bland\b/i;
// Mixed-use indicator: an explicit "mixed" or a slashed compound such as
// "Commercial/Residential". We never flatten these to a single use.
const MIXED_USE_RE = /mixed|\//i;

/**
 * resolvePropertyType reconciles the raw `property_type` string against
 * `property_class` and the physical structure on record. Structure wins:
 * a parcel with bedrooms is residential no matter what a label says, and a
 * parcel with a building is not vacant land.
 *
 * Conservative by design — it only overrides a label when there is concrete
 * contradicting evidence, so consistent rows are never second-guessed, and it
 * never invents a stronger claim than the evidence supports (a bare square-
 * footage signal yields "Improved Land", not "Residential").
 */
export function resolvePropertyType(property: Property): ResolvedType {
  const building = property.building;

  // Condominium override (#106) takes precedence — a parcel-level land use of
  // CONDOMINIUM is authoritative for the headline type.
  const landUse = (building?.landUseDesc || "").toUpperCase();
  if (landUse === "CONDOMINIUM") {
    return { label: "Condominium", conflict: false };
  }

  const rawType = (property.propertyType || "").trim();
  const klass = (property.propertyClass || "").trim().toLowerCase();
  const beds = building?.bedrooms ?? null;
  const sqft = building?.totalSqft ?? null;
  const year = building?.yearBuilt ?? null;
  // OneMap's authoritative improved-vs-vacant flag. THREE-STATE: true /
  // false / undefined, where undefined covers both the source's own "U"
  // (the assessor declining to say) and an absent key — see
  // BuildingView.hasStructure. Strengthens the structure signal.
  const structFlag = building?.hasStructure;

  const hasStructure =
    structFlag === true || (beds !== null && beds > 0) || (sqft !== null && sqft > 0) || (year !== null && year > 0);
  const residentialEvidence = klass === "residential" || (beds !== null && beds > 0);
  const isCommercialType = COMMERCIAL_TYPE_RE.test(rawType);
  const isMixedUse = MIXED_USE_RE.test(rawType);

  // Case 1: labeled commercial/industrial but the class + structure say
  // residential (6019 Ingleside). Trust the residential evidence — but never
  // flatten a mixed-use parcel ("Mixed Use", "Commercial/Residential"), and
  // never override a row whose class is itself commercial.
  if (isCommercialType && !isMixedUse && residentialEvidence && klass !== "commercial") {
    return {
      label: "Residential",
      conflict: true,
      note: `Source classified this as ${rawType}, but the parcel record (class + structure) indicates residential.`,
    };
  }

  // Case 2: class says vacant land but a structure is on record (2608 Golf
  // Course: land_only + 3 beds). Not vacant land — but only claim "Residential"
  // when there's residential evidence (beds, or a residential class). A bare
  // square-footage signal (e.g. an outbuilding on a lot) is "Improved Land",
  // not a house.
  if (klass === "land_only" && hasStructure) {
    let label: string;
    if (rawType && !VACANT_TYPE_RE.test(rawType)) {
      label = rawType; // a specific, non-land type the source already gave us
    } else if (residentialEvidence) {
      label = "Residential";
    } else {
      label = "Improved Land";
    }
    return {
      label,
      conflict: true,
      note: "Tax record lists this as vacant land, but a structure is on record.",
    };
  }

  // Case 3: class says residential but CAMA explicitly reports NO structure —
  // the inverse of Case 2 (1,094 prod rows measured 2026-09-16, vs. 166 for
  // Case 2's direction). Unlike Case 2, this deliberately does NOT relabel or
  // invent a different type: the underlying per-row cause (a stale
  // pre-demolition CAMA snapshot vs. a scraper parcel-matching miss picking up
  // the wrong parcel's building record) isn't knowable here, so we flag the
  // contradiction rather than guess which side is right. structFlag === false
  // is OneMap's EXPLICIT "no structure" — undefined ("U" or no flag) must not
  // trip this, same three-state rule as resolvePlayType above; an unknown flag
  // is not evidence of anything.
  const builtEvidence = (beds !== null && beds > 0) || (sqft !== null && sqft > 0) || (year !== null && year > 0);
  if (klass === "residential" && structFlag === false && builtEvidence) {
    return {
      label: "Residential",
      conflict: true,
      note: "County record shows no structure on this parcel — the beds/baths/sqft below may be stale.",
    };
  }

  // Consistent / insufficient evidence to override: show the raw type, falling
  // back to the class-derived label, then to "Vacant Land" when OneMap's struct
  // flag says there's no building AND nothing else suggests one (gives Cabarrus
  // legal-description rows an honest type instead of a blank — never overrides a
  // row that has any structure evidence).
  const label =
    rawType ||
    (klass ? propertyClassLabel(klass) : null) ||
    // === false is the source's EXPLICIT "no structure". undefined ("U", or
    // no flag) must not land here — see BuildingView.hasStructure.
    (structFlag === false && !hasStructure ? "Vacant Land" : null);
  return { label, conflict: false };
}

// ── investor signals from the OneMap value split (Thread A) ──────────────────

export interface PlayType {
  /** Short tag for the card/detail. */
  label: string;
  /** Why — for a tooltip / secondary line. */
  note: string;
}

/**
 * resolvePlayType derives an investor-oriented tag from OneMap's assessed-value
 * split (land vs improvement) + the has_structure flag (both stamped into
 * raw_data by the parcel-facts enrichment). Deliberately conservative — only two
 * tags, only when the data clearly supports them; returns null otherwise (an
 * ordinary improved home gets no tag, since the property type already describes
 * it). This is a signal, never a claim we can't stand behind.
 */
export function resolvePlayType(property: Property): PlayType | null {
  const building = property.building;
  const structFlag = building?.hasStructure;
  // Land/improvement split, COUNTY CAMA FIRST with OneMap as the statewide
  // fallback — ADR 0035. This file used to resolve the other way round
  // (om_* first) while PropertyDetail resolved county-first; the two
  // disagreed from the day they were written, and consolidating onto the
  // typed group forced one answer. The county assessor is the system of
  // record; OneMap is a statewide aggregation OF that record, so it fills
  // gaps rather than overriding the source.
  //
  // Gating rides along for free: every source key is denylisted, so
  // `valuation` is simply absent for a viewer whose plan does not cover the
  // county — exactly what the redacted raw_data read produced before.
  const land = property.valuation?.landValue ?? null;
  const improvement = property.valuation?.buildingValue ?? null;

  // A building exists if OneMap says so, or any structure evidence is on record
  // (guards against a stale/missing struct flag mislabeling a built parcel).
  const builtEvidence =
    structFlag === true ||
    (building?.bedrooms ?? 0) > 0 ||
    (building?.totalSqft ?? 0) > 0 ||
    (building?.yearBuilt ?? 0) > 0 ||
    (improvement ?? 0) > 0;

  // === false is an EXPLICIT "no structure on the parcel". undefined ("U",
  // or no flag at all) must NOT reach this branch — labelling an unknown
  // parcel "Vacant Land" is the exact error the three-state typing prevents.
  if (structFlag === false && !builtEvidence) {
    return { label: "Vacant Land", note: "No structure on the parcel record." };
  }

  // Land-value play: a building exists but its assessed improvement is a small
  // slice of total value — the worth is in the dirt (teardown / redevelop).
  if (builtEvidence && land !== null && improvement !== null && land + improvement > 0) {
    if (improvement / (land + improvement) < 0.25) {
      return {
        label: "Land-Value Play",
        note: "Most assessed value is land; the structure adds little (teardown / redevelop).",
      };
    }
  }
  return null;
}

/**
 * ownerHoldText turns OneMap's free-form last-sale text (saledatetx — e.g.
 * "8/2019", "2019", "08/15/2019") into a rough owner hold-duration chip
 * ("Owned ~6 yr"), a motivation/equity proxy (a long hold on a low basis tends
 * to mean deep equity). Returns null when no plausible year parses.
 */
export function ownerHoldText(property: Property): string | null {
  const txt = property.sale?.lastSaleText || null;
  if (!txt) return null;
  const m = txt.match(/(19|20)\d{2}/);
  if (!m) return null;
  const years = new Date().getFullYear() - parseInt(m[0], 10);
  if (years < 1 || years > 150) return null;
  return `Owned ~${years} yr`;
}

// ── completeness ────────────────────────────────────────────────────────────

export interface Completeness {
  /** Core fields with no usable value, in display order. */
  missing: string[];
  /** Human-readable data conflicts (e.g. a misclassified type). */
  conflicts: string[];
  /** present / applicable, 0..1. */
  score: number;
  /** Whether to surface a warning to the user. */
  warn: boolean;
  /**
   * When set, callers should show THIS instead of the generic "Limited data
   * — missing X, Y" phrasing. Present only when every missing field is
   * explained by the record's SOURCE TYPE (a legal notice that structurally
   * never states a dollar figure or a sale date) rather than a coverage gap
   * — so a well-covered county's notice-sourced row doesn't read as if the
   * county itself were thin. See NOTICE_ONLY_SOURCES below.
   */
  sourceLimitedNote?: string;
}

// Sources whose records are legal-notice text, not a county data feed — they
// structurally never carry amount_due (backend/internal/cron/ncnotices_promote.go
// hardcodes it NULL, see #1060) and usually carry no sale_date either (only
// present when the notice itself states one). A Mecklenburg row from THIS
// source showing "Limited data" is not a Mecklenburg coverage gap — the
// county's actual strong source is mecklenburg-arcgis/Kania, which is
// unaffected by this note.
const NOTICE_ONLY_SOURCES = new Set(["ncnotices"]);

/**
 * dataCompleteness reports which investor-relevant core fields are missing and
 * any cross-field conflicts. Investors filter on concrete parameters (year
 * built, size, etc.), so a missing field means the property is invisible to
 * that filter — better to say so than to render a confident blank.
 *
 * `warn` is true when ≥2 core fields are missing or any conflict exists; a
 * single gap on an otherwise-complete row isn't worth nagging about.
 */
export function dataCompleteness(property: Property): Completeness {
  const building = property.building;
  const landUse = (building?.landUseDesc || "").toUpperCase();
  const isCondo = landUse === "CONDOMINIUM";
  const klass = (property.propertyClass || "").trim().toLowerCase();

  const has = (v: number | null | undefined) => v !== null && v !== undefined && v > 0;
  const beds = has(building?.bedrooms);
  const baths = has(building?.fullBaths);
  const sqft = has(building?.totalSqft);
  const year = has(building?.yearBuilt);
  const lot = has(building?.lotSizeAcres) || has(building?.lotSizeSqft);
  const saleDate = !!property.auctionDate || !!property.sale?.saleDate;

  // Which core fields APPLY depends on the property kind — beds/baths are N/A
  // for commercial; beds/baths/sqft/year are N/A for vacant land; lot size is
  // N/A for a condo. Without this gating a fully-enriched office building would
  // falsely warn "missing beds, baths". Uses the reconciled type so a row
  // mislabeled "Commercial" but actually a house is treated as residential.
  const t = resolvePropertyType(property);
  const resolvedLabel = t.label || "";
  let kind: "residential" | "commercial" | "land";
  if (COMMERCIAL_TYPE_RE.test(resolvedLabel) || klass === "commercial") kind = "commercial";
  else if (klass === "land_only") kind = "land";
  else kind = "residential";

  // (label, present) — order is the display order in the warning.
  const fields: Array<[string, boolean]> = [["street address", isDeliverableAddress(property)]];
  if (kind === "residential") {
    fields.push(["beds", beds], ["baths", baths], ["square footage", sqft], ["year built", year]);
    if (!isCondo) fields.push(["lot size", lot]); // condos own no parcel-level land
  } else if (kind === "commercial") {
    fields.push(["square footage", sqft], ["year built", year], ["lot size", lot]);
  } else {
    fields.push(["lot size", lot]); // vacant land — beds/baths/sqft/year don't apply
  }
  fields.push(
    ["assessed value", has(property.assessedValue ?? null)],
    ["amount due", has(property.amountDue ?? null)],
    ["sale date", saleDate],
  );

  const missing = fields.filter(([, present]) => !present).map(([label]) => label);

  const conflicts: string[] = [];
  if (t.conflict && t.note) conflicts.push(t.note);

  const present = fields.length - missing.length;
  const score = fields.length > 0 ? present / fields.length : 1;

  // Only override the message when EVERY missing field is one a legal notice
  // structurally can't carry — if beds/sqft/etc. are also missing, that's a
  // real gap and the generic phrasing should stand.
  let sourceLimitedNote: string | undefined;
  if (NOTICE_ONLY_SOURCES.has(property.source || "") && missing.length > 0) {
    const explainable = new Set(["amount due", "sale date"]);
    if (missing.every((m) => explainable.has(m))) {
      sourceLimitedNote =
        missing.length === 2
          ? "Legal notice doesn't state an amount or sale date yet — check back once a firm publishes sale terms."
          : missing[0] === "amount due"
          ? "Legal notice doesn't state an amount owed — this is typical for a notice-sourced record, not a data gap."
          : "Legal notice doesn't state a sale date yet — check back once a firm publishes sale terms.";
    }
  }

  return {
    missing,
    conflicts,
    score,
    warn: missing.length >= 2 || conflicts.length > 0,
    sourceLimitedNote,
  };
}

// ── address deliverability (Zillow gating) ───────────────────────────────────

// Abbreviated legal-description tokens, anchored to legal-desc grammar (the
// token must be FOLLOWED by a number or a single block letter) so we don't
// false-match real street addresses — e.g. "STONY PT RD" / "CEDAR PT BLVD"
// (Point) must NOT trip, while "LT 28 …", "P/O 12 …", "BLK C" do. Slash
// notations and "NOT ADJACENT" are legal-desc-specific and need no anchor.
// NOTE: the city requirement in isDeliverableAddress is the PRIMARY backstop —
// spelled-out forms ("LOT 28", "SOUTH SIDE OF …") aren't matched here but are
// caught because legal-description rows carry no city. Don't relax the city
// check without strengthening this regex.
const LEGAL_DESC_RE =
  /\b(LTS?|PT|P\/O|BLK|ADDN)\s+(\d|[A-Z]\b)|\b(S\/S|N\/S|E\/S|W\/S)\b|NOT ADJ(ACENT)?/i;

/**
 * hasAddressContent is the content rule: does this string say anything at all?
 *
 * It exists because empty is not the same as blank here. OneMap joins empty
 * address components into values like ",," — a TRUTHY string, so
 * `street || "Unknown Address"` never fired and the card rendered a literal ",,"
 * (#1064 defect 2). Every real address, including a bare street name with no
 * house number ("CHINA AVE" — an unimproved parcel, which is upstream truth and
 * must still render), contains at least one alphanumeric character.
 *
 * The SAME predicate runs on the write side: admissibleAddress and
 * nullIfContentless in backend/internal/cron, plus the `~ '[A-Za-z0-9]'` gates
 * in leads_repository.go. One rule, both ends of the pipe.
 */
export function hasAddressContent(s: string | null | undefined): boolean {
  return /[A-Za-z0-9]/.test(s || "");
}

/**
 * looksLikeLegalDescription reports whether the address is a deed legal
 * description rather than a mailable street address. Cabarrus stores these in
 * raw_data.legal_description; some also leak into the address field
 * ("LT 28 KENSINGTON FOREST", "S/S KNOWLES ST", "EAST OF BUT NOT ADJACENT TO …").
 */
export function looksLikeLegalDescription(property: Property): boolean {
  const legal = property.situs?.legalDescription || null;
  const addr = (property.address || "").trim();
  if (legal && (!hasAddressContent(addr) || addr === legal)) return true;
  return LEGAL_DESC_RE.test(addr);
}

/**
 * isDeliverableAddress reports whether we can build a trustworthy external
 * link (Zillow) from the address. Requires a real street address plus a CITY
 * to disambiguate locality — a bare street name resolves to the wrong city
 * nationwide ("Waterfowl Ln" → Chardon OH; a Cabarrus legal-desc row → a
 * Gastonia result). Fewer links, all correct (the founder's stated preference).
 */
export function isDeliverableAddress(property: Property): boolean {
  // A point-in-polygon-derived NG911 situs (NC OneMap AddressNC) is a real,
  // mailable address that carries its own city/zip — deliverable even when
  // properties.city is empty (Cabarrus rows have no city column value).
  if (hasAddressContent(property.situs?.address ?? null) && property.situs?.city) return true;
  const addr = (property.address || "").trim();
  // #1064 defect 2: not `!addr` — ",," is truthy, and building a Zillow link
  // from it would send the user to an arbitrary result.
  if (!hasAddressContent(addr)) return false;
  if (looksLikeLegalDescription(property)) return false;
  if (!(property.city || "").trim()) return false;
  return true;
}

// ── display address (real situs over legal description) ──────────────────────

export interface DisplayAddress {
  /** The address line to show. */
  text: string;
  /** True when this is an inferred situs from NC OneMap AddressNC — show provenance. */
  isDerivedSitus: boolean;
  /** True when we're falling back to a deed legal description (no real address). */
  isLegalDescription: boolean;
  /** City / zip for the derived situs, when known. */
  city?: string;
  zip?: string;
}

/**
 * resolveDisplayAddress picks the best address line for a property. A
 * point-in-polygon-derived situs from NC OneMap AddressNC (stored in
 * raw_data.derived_situs_address — see CabarrusSitusOverlay) wins over
 * properties.address, which for legal-description-only counties (Cabarrus) is
 * the deed text. Callers MUST show a provenance note when isDerivedSitus is
 * true, so an inferred address is never presented as if the foreclosure filing
 * stated it.
 */
export function resolveDisplayAddress(property: Property): DisplayAddress {
  const typedCity = (property.city || "").trim();
  const typedZip = (property.zipCode || "").trim();
  const derived = property.situs?.address ?? null;
  if (derived && hasAddressContent(derived)) {
    return {
      text: derived,
      isDerivedSitus: true,
      isLegalDescription: false,
      city: typedCity || property.situs?.city || undefined,
      zip: typedZip || property.situs?.zip || undefined,
    };
  }
  const addr = (property.address || "").trim();
  const legal = looksLikeLegalDescription(property);

  // Firm/portal/Kania rows often store "Street, City" inline with no separate
  // city column (e.g. "116 Dorton Street, Charlotte"), while ArcGIS rows store
  // a clean street + a city column. To make the card render uniformly, when
  // there's no typed city and the address is not a legal description, split a
  // trailing ", City" off so the white line is street-only and the meta line
  // gets the city — matching the clean rows. Conservative: only when the tail
  // is short and digit-free (so we never mangle a unit/suite number).
  let street = addr;
  let parsedCity = "";
  if (!typedCity && !legal && addr.includes(",")) {
    const idx = addr.lastIndexOf(",");
    const head = addr.slice(0, idx).trim();
    const tail = addr.slice(idx + 1).trim();
    if (head && tail && tail.length <= 28 && !/\d/.test(tail)) {
      street = head;
      parsedCity = tail;
    }
  }

  // Cabarrus rows carry NO address column value as of #1064 defect 4 (the deed
  // legal description is no longer copied into properties.address — it stays in
  // raw_data.legal_description). Render it from there, still flagged, so the
  // card shows what the filing actually says instead of "Unknown Address".
  const legalText = property.situs?.legalDescription || null;
  if (!hasAddressContent(street) && legal && legalText) {
    return {
      text: legalText,
      isDerivedSitus: false,
      isLegalDescription: true,
      city: typedCity || property.situs?.city || undefined,
      zip: typedZip || property.situs?.zip || undefined,
    };
  }

  return {
    // NOT `street || …` — ",," is truthy, which is the whole of #1064 defect 2.
    text: hasAddressContent(street) ? street : "Unknown Address",
    isDerivedSitus: false,
    isLegalDescription: legal,
    city: typedCity || parsedCity || property.situs?.city || undefined,
    zip: typedZip || property.situs?.zip || undefined,
  };
}

// ── parcel id honesty (synthetic CASE: ids) ─────────────────────────────────
//
// Some sources (Brock & Scott, historical Kania) don't publish a real
// county-issued parcel number. For those rows properties.parcel_id holds a
// synthetic CASE:{court_file_number} placeholder instead (see the CLAUDE.md
// gotcha on parcel_id uniqueness) — it exists so UNIQUE(county_id,
// parcel_id) still holds, never to be shown to a user as if it were a real
// Assessor Parcel Number. Issue #687 / ADR 0023 explicitly rejected
// rewriting parcel_id to a real APN — this is a display-layer fix only.

const SYNTHETIC_PARCEL_ID_PREFIX = "CASE:";

/**
 * isSyntheticParcelId reports whether a parcelId is our internal
 * CASE:{court_file_number} placeholder rather than a real, county-issued
 * parcel number.
 */
export function isSyntheticParcelId(parcelId: string | null | undefined): boolean {
  return !!parcelId && parcelId.startsWith(SYNTHETIC_PARCEL_ID_PREFIX);
}

export interface ResolvedParcelId {
  /** The parcel id to display, or null when we withhold it — see note. */
  value: string | null;
  /** True when property.parcelId is the synthetic CASE: placeholder. */
  isSynthetic: boolean;
  /** Reason for the unavailable state; present only when value is null. */
  note?: string;
}

/**
 * resolveDisplayParcelId decides what a "Parcel ID" field should show: the
 * real parcel number when we have one, or an honest "unavailable"
 * explanation when property.parcelId is the synthetic CASE: placeholder.
 *
 * Never returns the raw placeholder (that would present a court file
 * number under a "Parcel ID" label) and never strips "CASE:" to show the
 * bare number (the same lie with better formatting — the number IS a
 * court file number, not a parcel id, no matter how it's formatted).
 */
export function resolveDisplayParcelId(property: Property): ResolvedParcelId {
  const id = (property.parcelId || "").trim();
  if (!id) {
    return { value: null, isSynthetic: false, note: "Parcel ID not published by the source" };
  }
  if (isSyntheticParcelId(id)) {
    return {
      value: null,
      isSynthetic: true,
      note: "Not published by the source — this filing lists a court file number, not a county parcel ID",
    };
  }
  return { value: id, isSynthetic: false };
}

// ── never-empty key numbers (three-state) ──────────────────────────────────
//
// The founder's rule: assessed value + amount owed (+ opening/current bid)
// should never render as a blank or a misleading "$0". But some counties
// genuinely don't publish a field, so we use three honest states instead of
// faking a number:
//   - known: a real source value.
//   - derived: a stand-in we can defend, clearly labeled (e.g. the opening bid
//     IS the de-facto amount owed for the Kania-firm counties, which publish no
//     separate "amount due").
//   - unavailable: nothing to show — say so with a reason, never "$0"/"N/A".

export type FieldState = "known" | "derived" | "unavailable";

export interface ResolvedAmount {
  /** Numeric value to format, or null when unavailable. */
  amount: number | null;
  state: FieldState;
  /** Short reason/label for the derived or unavailable case. */
  note?: string;
  /**
   * Which figure supplied `amount`. Set only by resolveAmountOwed, so consumers
   * that score the debt (dealScore's equity factor) can say where it came from
   * without re-deriving the fallback order.
   */
  source?: AmountOwedSource;
}

/**
 * amount_due: a published amount owed. published_bid: amount_due promoted from
 * a bid key by cron.NormalizeAmountDue. opening_bid: the raw opening bid.
 * original_principal: the deed-of-trust ORIGINAL loan amount (Path B, #1672).
 */
export type AmountOwedSource = "amount_due" | "published_bid" | "opening_bid" | "original_principal";

// resolveAssessedValue: the county assessor value when present, else an honest
// "unavailable" (never a $0).
export function resolveAssessedValue(property: Property): ResolvedAmount {
  if (property.assessedValue && property.assessedValue > 0) {
    return { amount: property.assessedValue, state: "known" };
  }
  return { amount: null, state: "unavailable", note: "County assessed value not yet available" };
}

// resolveMarketValue: what the property is plausibly WORTH — the second of the
// two questions an auction buyer actually has (the first, what it will cost, is
// resolveAmountOwed above).
//
// Two sources, deliberately ranked and always labeled:
//
//  1. est_market_value — the county CAMA vendor's own market estimate, written
//     by cmd/spatialest-enrich (#445). The better number, but thin: measured
//     against prod 2026-08-31, only ~12% of auctions in the next 21 days carry
//     it, because it requires a CAMA-wired county AND a successful nightly run.
//  2. assessedValue — the tax-roll assessed value, present on ~84% of that same
//     cohort. NOT the same thing as market value and must never be presented as
//     if it were: NC counties revalue on a 4-8 year cycle (G.S. 105-286), so in
//     an appreciating market an assessed value is typically BELOW market, and
//     the gap widens the further you are from the county's last revaluation.
//
// Returning `derived` (not `known`) for branch 2 is the whole point — a viewer
// sizing a bid needs to know whether they're looking at a market estimate or a
// stale tax figure. Collapsing them into one unlabeled number is how someone
// overbids.
//
// Deliberately NOT computing an equity/spread figure here: that needs a
// trustworthy market value AND a trustworthy amount owed, and resolveAmountOwed
// already documents how often the latter is a derived original-principal
// figure rather than the real payoff. Multiplying two soft numbers into a
// confident-looking "equity" is a worse error than showing neither.
export function resolveMarketValue(property: Property): ResolvedAmount {
  const est = property.estMarketValue;
  if (typeof est === "number" && est > 0) {
    const src = (property.estMarketValueSource || "").trim();
    return {
      amount: est,
      state: "known",
      note: src ? `County CAMA estimate (${src})` : "County CAMA estimate",
    };
  }
  if (property.assessedValue && property.assessedValue > 0) {
    return {
      amount: property.assessedValue,
      state: "derived",
      note:
        "Tax-assessed value, not a market estimate — NC counties revalue every " +
        "4-8 years, so this usually trails the market and the gap grows with " +
        "time since the last revaluation. Treat it as a floor, not a comp.",
    };
  }
  return {
    amount: null,
    state: "unavailable",
    note: "No county market estimate or assessed value on file for this parcel",
  };
}

// resolveAmountOwed: the published amount due when the county provides it;
// otherwise the opening bid as the de-facto figure (Kania-firm counties publish
// no separate amount owed — the opening bid is that number), clearly labeled;
// otherwise the extracted deed-of-trust ORIGINAL principal for mortgage
// foreclosures where we've been able to parse it out of the Notice of Sale /
// Notice of Hearing text (Path B, #1672); otherwise unavailable.
//
// The "unavailable" note is source-aware. Mortgage foreclosures (Shapiro,
// Brock & Scott, Hutchens, RBCWB) don't publish an opening bid pre-sale by
// design — the lender's attorney sets it the morning of the sale (typically
// full debt / bid-at-par, but sometimes lower to draw third-party bidders).
// A blank amount there is EXPECTED, not a data gap; telling a viewer to
// "check the county" would be misdirection. Tax foreclosures (Kania,
// forsyth_tax, etc.) DO carry a deterministic opening bid = tax debt +
// costs; a blank there IS a data gap worth flagging.
//
// The "derived from original DOT principal" branch is the Path B win —
// backend/internal/cron/mortgage_principal.go extracts the original loan
// amount from Notice of Sale text into raw_data.original_principal_amount.
// See that file's package header for the load-bearing caveat: this is the
// ORIGINAL loan amount, not the current amount owed, and the display note
// must say so or the user will misbid.
// Per-source wording for a promoted amount_due (see resolveAmountOwed). Keyed
// by the raw_data key cron.NormalizeAmountDue recorded in amount_due_source.
// Tax-side and mortgage-side bids fail in different directions, so they don't
// share a sentence: a tax opening bid is the debt, a firm's bid is the
// attorney's opening number.
const BID_SOURCE_NOTES: Record<string, string> = {
  opening_bid: "From the opening bid — this county publishes no separate amount owed",
  brockscott_opening_bid:
    "From Brock & Scott's published opening bid — the lender's attorney sets the " +
    "final figure at the sale and may open below the full debt to attract bidders",
  hutchens_bid_amount:
    "From Hutchens' published bid figure — the lender's attorney sets the final " +
    "figure at the sale and may open below the full debt to attract bidders",
};

export function resolveAmountOwed(property: Property): ResolvedAmount {
  if (property.amountDue && property.amountDue > 0) {
    // sale.amountDueSource is stamped by cron.NormalizeAmountDue when the typed
    // column was PROMOTED out of a raw_data key rather than published as an
    // amount owed in its own right. It matters here because an opening bid is
    // not the same claim as a debt: for tax foreclosures it's the de-facto
    // amount owed (tax debt + costs), but for mortgage foreclosures the
    // lender's attorney sets it at the sale and may set it BELOW full debt to
    // draw third-party bidders.
    //
    // Without this branch, promoting those figures into the typed column would
    // silently upgrade them from the "derived" label the raw_data path below
    // already gives them to a bare authoritative-looking number — losing the
    // caveat on exactly the figure people size bids against. (The server
    // surfaces this as a typed field; raw_data left the wire in #1872.)
    // Rows with no
    // marker (every pre-existing row, and anything a scraper wrote directly)
    // keep reading as "known", unchanged.
    const promotedFrom = property.sale?.amountDueSource ?? "";
    if (promotedFrom && promotedFrom !== "due_amount") {
      return {
        amount: property.amountDue,
        state: "derived",
        note: BID_SOURCE_NOTES[promotedFrom] ?? "From a published bid figure, not a separately stated amount owed",
        source: "published_bid",
      };
    }
    return { amount: property.amountDue, state: "known", source: "amount_due" };
  }
  const opening = property.sale?.openingBid ?? null;
  if (opening && opening > 0) {
    return {
      amount: opening,
      state: "derived",
      note: "From the opening bid — this county publishes no separate amount owed",
      source: "opening_bid",
    };
  }
  const originalPrincipal = property.sale?.originalPrincipalAmount ?? null;
  if (originalPrincipal && originalPrincipal > 0) {
    return {
      amount: originalPrincipal,
      state: "derived",
      source: "original_principal",
      note:
        "ORIGINAL loan amount from the deed of trust — not the current amount owed. " +
        "The lender's attorney sets the actual opening bid at the sale. Amount at " +
        "auction will differ: older loans may be materially paid down; cases in default " +
        "will have accrued interest, late fees, and attorney fees on top. Extracted from " +
        "the Notice of Sale / Notice of Hearing filed in the court case.",
    };
  }
  if (isMortgageForeclosureProperty(property)) {
    return {
      amount: null,
      state: "unavailable",
      note: "NC mortgage foreclosures don't publish an opening bid in advance — the lender's attorney sets it at the sale (typically the full debt). Check the court file for the deed-of-trust principal, or wait for the trustee's report of sale.",
    };
  }
  if (isTaxDelinquentProperty(property)) {
    // Deliberately NOT "not published by the county". The county DOES publish
    // this figure -- it is in the delinquent-tax export we ingest daily. The
    // row simply has not been matched to it yet: parcels the resolver mints
    // only receive their amount and years-behind on the FOLLOWING day's
    // ingestion (see cmd/ncpts-delinquent's runPass doc comment). Telling a
    // wholesaler the data does not exist, when it does and is arriving, is the
    // more damaging error of the two.
    return {
      amount: null,
      state: "unavailable",
      note:
        "Confirmed delinquent on county tax records, but the amount has not been " +
        "retrieved for this parcel yet. The county publishes it; expect it on the " +
        "next daily refresh.",
    };
  }
  return { amount: null, state: "unavailable", note: "Amount owed not published by the county" };
}

// isTaxDelinquentProperty — the parcel carries a delinquent-tax signal, so an
// absent amount means "not fetched yet", not "does not exist". Mirrors
// isMortgageForeclosureProperty's shape, including the property.source
// fallback for pre-signal-era rows.
const TAX_DELINQUENT_SOURCES = new Set([
  "ncpts_delinquent",
  "wake_tax_delinquent",
]);

function isTaxDelinquentProperty(property: Property): boolean {
  for (const s of property.leadSignals ?? []) {
    if (s.signalType === "tax_delinquent") return true;
    if (TAX_DELINQUENT_SOURCES.has(s.source)) return true;
  }
  if (property.source && TAX_DELINQUENT_SOURCES.has(property.source)) return true;
  return false;
}

// Mortgage-side sources that don't publish opening bid pre-sale. Kept as an
// inline list rather than imported from leadsignals — this is a display-side
// distinction, and importing from a Go-shape mirror would be premature.
// Matches on lowercase signal.source; also honors property.source as a
// fallback because pre-signal-era rows carry the source there.
const MORTGAGE_FORECLOSURE_SOURCES = new Set([
  "shapiro",
  "brockscott",
  "hutchens",
  "rbcwb",
]);

function isMortgageForeclosureProperty(property: Property): boolean {
  for (const s of property.leadSignals ?? []) {
    if (s.signalType === "mortgage_foreclosure") return true;
    if (MORTGAGE_FORECLOSURE_SOURCES.has(s.source)) return true;
  }
  if (property.source && MORTGAGE_FORECLOSURE_SOURCES.has(property.source)) return true;
  return false;
}

// parseRawDate parses a date string in the shapes our sources emit — Kania's
// MDY ("7/6/2026") or ISO ("2026-07-06") — into a local Date at midnight.
// Returns null when unparseable.
function parseRawDate(s: string | null): Date | null {
  if (!s) return null;
  const mdy = s.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (mdy) {
    const d = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
    return isNaN(d.getTime()) ? null : d;
  }
  const iso = s.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// ── NC court time ─────────────────────────────────────────────────────────────
// An upset-bid window ends at 5:00 PM Eastern on its last day (G.S. 1-339.25),
// the rule backend courtcal.CloseOfBusiness implements. Both the day boundary
// and the 5 PM cut-off are Eastern wall-clock, NOT the viewer's browser zone:
// before #2295 "today" came from the browser, so a viewer in California saw a
// window close 3 hours late and one in Europe saw it close before 5 PM Eastern,
// and the close day itself stayed "open" until local midnight rather than 5 PM.

const NC_COURT_TIME_ZONE = "America/New_York";
const CLOSE_OF_BUSINESS_MINUTES = 17 * 60;

const easternFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: NC_COURT_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// easternNow returns `now` as an Eastern calendar day (YYYYMMDD as a number, so
// days compare with < and >) plus minutes since Eastern midnight.
function easternNow(now: Date): { day: number; minutes: number } {
  const parts = easternFormatter.formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return {
    day: get("year") * 10000 + get("month") * 100 + get("day"),
    minutes: get("hour") * 60 + get("minute"),
  };
}

// calendarDay reads a parseRawDate result back as YYYYMMDD. parseRawDate builds
// the Date from the source's own year/month/day in local time, so local getters
// recover exactly those numbers whatever zone the browser is in.
function calendarDay(d: Date): number {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// upsetWindowOpen: open until 5:00 PM Eastern on the close date.
function upsetWindowOpen(close: Date, now: Date): boolean {
  const today = easternNow(now);
  const closeDay = calendarDay(close);
  return closeDay > today.day || (closeDay === today.day && today.minutes < CLOSE_OF_BUSINESS_MINUTES);
}

// resolveDisplayStatus returns the status key the UI should RENDER. It re-resolves
// the two states that hinge on the 10-day upset-bid window (G.S. 1-339.25; mirrors
// backend status.UpsetWindow) using the upset-window CLOSE date (raw_data.close_date,
// which Kania publishes), so the display stays correct even when the backend status
// lags. The rule is symmetric:
//   - close date in the FUTURE  -> upset window OPEN  -> "auction_scheduled" (an
//     active auction). This UPGRADES a row frozen at "auction_passed" by an older
//     run back to active — the bug a user caught: 326 Kirby / 5705 Woodard had a
//     future close date but showed "Auction Passed".
//   - close date in the PAST    -> window closed       -> "auction_passed".
//   - NO close date known        -> leave the status as-is (conservative; never
//     infer a conclusion from the bare sale date — e.g. RBCWB rows that don't store
//     a close date stay whatever the backend set).
// Only auction_scheduled / auction_passed are re-resolved; sold/redeemed/etc. and a
// portal-confirmed disposition (set by the backend transitioner) pass through
// untouched. Pure display fallback; never mutates data. "Future" means before
// 5:00 PM Eastern on the close date (upsetWindowOpen). `now` is injectable for tests.
// Takes the structural slice it reads (status + the close date) rather than a
// whole Property so the map can resolve a pin's colour from a slim MapPoint
// with the exact same function the list card uses.
export function resolveDisplayStatus(
  property: Pick<Property, "status"> & { sale?: { upsetWindowCloseAt?: string } },
  now: Date = new Date()
): string {
  const status = property.status || "";
  if (status !== "auction_scheduled" && status !== "auction_passed") return status;

  const close = parseRawDate(property.sale?.upsetWindowCloseAt ?? null);
  if (!close) return status; // no upset-window signal → leave as-is

  return upsetWindowOpen(close, now) ? "auction_scheduled" : "auction_passed";
}

// hasUpsetWindowEvidence reports whether the data can back an "upset window
// OPEN" claim for an auction row. An upset window only exists AFTER a sale, so:
//   - close date known  -> open until 5:00 PM Eastern on it (Kania-style, authoritative)
//   - no close date, sale date in the FUTURE -> false: no sale has happened yet
//   - no close date, no sale date at all      -> false: nothing says a sale happened
//   - no close date, sale date today or past (Eastern) -> true: defer to the backend status,
//     which ages a stale row out and holds one open only on real upset activity
// This only ever WITHHOLDS a positive claim. It never infers "closed" from a bare
// sale date, which resolveDisplayStatus above deliberately refuses to do.
//
// Measured 2026-09-14: of 49 auction_scheduled cards showing "Upset window open"
// (every row with a recorded current bid), 42 were backed by a future Kania/RBCWB
// close date and true; 7 Guilford rows were not, 5 with a sale not until
// 2026-10-07 and 2 with no sale date at all. `now` is injectable for tests.
export function hasUpsetWindowEvidence(property: Property, now: Date = new Date()): boolean {
  const close = parseRawDate(property.sale?.upsetWindowCloseAt ?? null);
  if (close) return upsetWindowOpen(close, now);

  const sale = parseRawDate(property.auctionDate ?? null);
  if (!sale) return false;
  return calendarDay(sale) <= easternNow(now).day;
}

// ── auction vs lead headline badge ──────────────────────────────────────────

export interface PropertyBadge {
  /** Compact text for the card pill (statusMeta's "short" for a status, the
   *  lead-type label for a lead — lead labels are already short). */
  short: string;
  /** Full text for detail views. */
  label: string;
  /** Color, sourced from statusMeta.ts or leadTypeMeta.ts — never a new hex. */
  color: string;
  /** Tooltip / help text. */
  desc: string;
  /** True when this badge shows a LEAD TYPE rather than a lifecycle STATUS. */
  isLeadType: boolean;
  /** Human labels for any additional signals beyond the one shown headline
   *  (multi-reason leads, e.g. both tax_foreclosure and probate) — render as
   *  a "+N" indicator with these in the tooltip. Undefined when there's only
   *  the one (or zero) signal. */
  otherSignalLabels?: string[];
  /** For an in-auction property only: the foreclosure TYPE (Mortgage
   *  Foreclosure, Tax Foreclosure, HOA Foreclosure, ...) as a secondary tag
   *  alongside the status headline. NC handles these very differently
   *  (mortgage is non-judicial power-of-sale with no redemption; tax carries
   *  an upset-bid period and redemption rights) — before this, that
   *  distinction disappeared entirely once a property left the pre-auction
   *  lead stage, and a reader had to infer it from the source scraper name.
   *  Status stays primary; this is never set when inAuction is false (the
   *  full badge already IS the type then), and undefined when no signal is
   *  on record — never fabricated. */
  foreclosureTypeLabel?: string;
  foreclosureTypeColor?: string;
}

/**
 * resolvePropertyBadge picks what a property card / detail panel should
 * show as its headline pill:
 *  - inAuction (a live auction; also the default when the field is absent,
 *    so older payloads/tests keep today's behavior) -> the existing
 *    lifecycle STATUS, unchanged.
 *  - a pre-auction LEAD (inAuction === false) -> the highest-priority
 *    leadSignals[].signalType, human-labeled (lib/leadTypeMeta.ts), so the
 *    card answers "why is this here" instead of a status that doesn't apply
 *    yet.
 *  - a lead with no signals recorded falls back to the status label rather
 *    than a blank pill (data-honesty rule: never fabricate, but never blank
 *    either).
 *
 * `activeLeadTypeFilter` (optional) is the lead-type filter currently applied
 * to the list this card is rendered in (e.g. "mortgage_foreclosure"). When a
 * property carries multiple signals AND one matches the active filter, THAT
 * signal becomes the headline pill instead of the fixed-priority default —
 * otherwise a property surfaced by filtering for "Mortgage Foreclosure" could
 * show "TAX FORECLOSURE +1" as its headline, reading as though the filter
 * matched the wrong reason. The non-headline signals still fold into
 * otherSignalLabels/+N exactly as before. No filter (or no match) falls back
 * to the original priority-ranked pick, unchanged.
 */
export function resolvePropertyBadge(
  property: Property,
  activeLeadTypeFilter?: string | string[]
): PropertyBadge {
  const sMeta = statusMeta(resolveDisplayStatus(property));
  const statusBadge: PropertyBadge = {
    short: sMeta.short,
    label: sMeta.label,
    color: sMeta.color,
    desc: sMeta.desc,
    isLeadType: false,
  };
  if (property.inAuction !== false) {
    // Secondary type tag — see PropertyBadge.foreclosureTypeLabel's doc.
    // Reuses the same priority-ranked pick as the pre-auction path so the
    // tag agrees with whatever that path would have shown as headline had
    // this property not yet reached auction.
    const primaryType = primaryLeadSignal(property.leadSignals);
    if (primaryType) {
      const lt = leadTypeMeta(primaryType.signalType);
      statusBadge.foreclosureTypeLabel = lt.label;
      statusBadge.foreclosureTypeColor = lt.color;
    }
    return statusBadge;
  }

  const signals = property.leadSignals || [];
  // With several lead types checked, the headline is the highest-priority of
  // the signals that match one of them.
  const wanted = Array.isArray(activeLeadTypeFilter)
    ? activeLeadTypeFilter
    : activeLeadTypeFilter
      ? [activeLeadTypeFilter]
      : [];
  const filterMatch =
    wanted.length > 0 ? primaryLeadSignal(signals.filter((s) => wanted.includes(s.signalType))) : null;
  const primary = filterMatch ?? primaryLeadSignal(signals);
  if (!primary) return statusBadge;

  const lt = leadTypeMeta(primary.signalType);
  const others = signals
    .filter((s) => s !== primary)
    .map((s) => leadTypeMeta(s.signalType).label);

  // #2254: a code_enforcement signal carries a SPECIFIC violation
  // classification (backend codeviolation.Class.Label(), e.g. "Repair
  // order", "Minimum housing", "Housing — unsafe/unfit") computed at ingest
  // time but, until now, never surfaced — every code-violation lead showed
  // the identical generic "Code Violation" pill whether it was a boarded-up
  // condemnation candidate or a routine nuisance case (found in a customer
  // demo: "is this a teardown or a weeds ticket?" was unanswerable from the
  // card). primary.violationLabel is only ever populated for
  // code_enforcement/condemnation rows (see property_lead_signals.raw_data's
  // code_violation_label); every other lead type falls through to the
  // existing generic lt.label unchanged.
  //
  // condemnation itself already gets its own distinct "Condemned" pill +
  // color from leadTypeMeta (the strongest hue in the palette, and
  // LEAD_SIGNAL_PRIORITY's #1 rank), so it is deliberately left alone here —
  // that is already the unmistakable treatment the underlying issue asks
  // for. This only sharpens the generic code_enforcement bucket, which is
  // where "teardown vs weeds ticket" was actually unanswerable.
  const headline =
    primary.signalType === "code_enforcement" && primary.violationLabel
      ? primary.violationLabel
      : lt.label;

  // #1957: subStatus (e.g. "8 violations since 2019", written by every
  // signal source — see backend codeviolation.Summary.SubStatus) was being
  // computed and stored but never rendered anywhere. Folding it into the
  // headline description is what makes "8 violations since 2019" and "a
  // single 2019 complaint" actually look different in the product, instead
  // of both showing an identical "Code Violation" pill.
  let desc = `Lead signal: ${headline}`;
  if (primary.subStatus) desc += ` — ${primary.subStatus}`;
  if (others.length > 0) desc += ` (also: ${others.join(", ")})`;

  return {
    short: headline,
    label: headline,
    color: lt.color,
    desc,
    isLeadType: true,
    otherSignalLabels: others.length > 0 ? others : undefined,
  };
}
